/**
 * Writing or correcting one shelf question — the `+` and the pencil on `/raporty/katalog`.
 *
 * The fields are `QuestionFields`, the same ones the editor's card uses, with one difference that is
 * the caller's and not the fields': the recording type is not offered — a question naming one
 * movement is not reusable, and `CustomQuestionRules` refuses to save one.
 *
 * „Jednostka" used to be the second difference and was removed on 17 September 2026: the column
 * exists on `PostTrainerQuestionRequest`, and nothing on this side can read the value back — the
 * copy into a template drops it and this screen never prints it. See `AGENTS.md`.
 *
 * ── The footer, since 17 September 2026 ──────────────────────────────────────────────────────
 * „Anuluj" is the canon's plain text and the primary is a SQUARE carrying a floppy — the owner's
 * call, with the answer-type chips beside it: both changes are about a 460px dialog that had been
 * asked to hold two long words at one end of a row and four long ones across a four-column grid.
 * The chips wrap to their own widths now and the primary says its whole name on the control.
 *
 * ── Editing a shelf entry does not touch the surveys that used it ────────────────────────────
 * R12 and R35: a survey holds its own copy. The dialog says so under the title when it is opened on
 * a question something already uses, because the opposite is the natural assumption and a trainer
 * correcting a typo deserves to know it will not reach the forms already out there.
 */

import { useState, type RefObject } from 'react';
import { Dialog } from '@base-ui/react/dialog';

import { CrossIcon } from '~/shared/ui/AuthIcons';
import ember from '~/shared/ui/emberButton.module.css';
import ghost from '~/shared/ui/ghostButton.module.css';
import { SaveGlyph } from '~/shared/ui/SaveGlyph';

import type { ShelfDraft } from '../lib/useQuestionShelf';
import type { AuthorableAnswerType } from '../model/editorDraft';
import { AUTHORABLE_ANSWER_TYPES } from '../model/editorDraft';
import type { ShelfQuestion } from '../model/questionCatalog';
import {
  EMPTY_QUESTION_FIELDS,
  normalizedOptions,
  questionFieldsComplete,
  type QuestionFieldsValue,
} from '../model/questionFields';
import type { TranslateKey } from '../model/translateKey';
import pickerStyles from './PickerDialog.module.css';
import { QuestionFields } from './QuestionFields';
import styles from './EditorForm.module.css';

function isAuthorable(value: string): value is AuthorableAnswerType {
  return (AUTHORABLE_ANSWER_TYPES as readonly string[]).includes(value);
}

/** An existing row as the fields hold it. A type the shelf holds but cannot author falls back. */
function fieldsOf(question: ShelfQuestion | null): QuestionFieldsValue {
  if (question === null) return EMPTY_QUESTION_FIELDS;
  return {
    label: question.label,
    answerType: isAuthorable(question.answerType) ? question.answerType : 'ShortText',
    options: question.options.length > 0 ? [...question.options] : ['', ''],
    required: question.required,
  };
}

export interface ShelfQuestionDialogProps {
  /** `'new'` for the `+`, the row for the pencil, `null` when the dialog is closed. */
  readonly editing: ShelfQuestion | 'new' | null;
  readonly busy: boolean;
  readonly onSubmit: (draft: ShelfDraft, id: number | null) => void;
  readonly onClose: () => void;
  readonly triggerRef: RefObject<HTMLElement | null>;
  readonly t: TranslateKey;
}

export function ShelfQuestionDialog({
  editing,
  busy,
  onSubmit,
  onClose,
  triggerRef,
  t,
}: ShelfQuestionDialogProps) {
  const existing = editing === 'new' || editing === null ? null : editing;

  return (
    <Dialog.Root
      open={editing !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={pickerStyles.backdrop} />
        <Dialog.Popup className={pickerStyles.popup} finalFocus={triggerRef}>
          {/* KEYED by which row the dialog was opened on. The form's state is then React's to
            * reset — a new key is a new element — rather than an effect racing a refetch of the
            * shelf for the right to say what is in the fields. */}
          <ShelfForm
            key={existing === null ? 'new' : String(existing.id)}
            existing={existing}
            busy={busy}
            onSubmit={onSubmit}
            onClose={onClose}
            t={t}
          />
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ShelfForm({
  existing,
  busy,
  onSubmit,
  onClose,
  t,
}: {
  readonly existing: ShelfQuestion | null;
  readonly busy: boolean;
  readonly onSubmit: (draft: ShelfDraft, id: number | null) => void;
  readonly onClose: () => void;
  readonly t: TranslateKey;
}) {
  const [value, setValue] = useState<QuestionFieldsValue>(() => fieldsOf(existing));

  const complete = questionFieldsComplete(value, { clipsLeft: 0 });
  const saveLabel =
    existing === null
      ? t('web:reports.catalog.dialog.create')
      : t('web:reports.catalog.dialog.save');

  return (
    <>
  <div className={pickerStyles.head}>
        <div className={pickerStyles.headText}>
          <Dialog.Title className={pickerStyles.title}>
            {existing === null
              ? t('web:reports.catalog.dialog.newTitle')
              : t('web:reports.catalog.dialog.editTitle')}
          </Dialog.Title>
          <p className={pickerStyles.lede}>
            {existing !== null && existing.usedIn.length > 0
              ? t('web:reports.catalog.dialog.editNote', { count: existing.usedIn.length })
              : t('web:reports.catalog.dialog.lede')}
          </p>
        </div>
        <Dialog.Close
          className={pickerStyles.close}
          aria-label={t('web:reports.editor.picker.close')}
        >
          <CrossIcon className={pickerStyles.closeIcon} />
        </Dialog.Close>
      </div>

      <QuestionFields
        value={value}
        onChange={setValue}
        allowClip={false}
        clipsLeft={0}
        t={t}
      />

      <div className={styles.newFoot}>
        <button
          type="button"
          className={`${String(ghost.ghost)} ${String(styles.newCancel)}`}
          onClick={onClose}
        >
          {t('web:reports.editor.newQuestion.cancel')}
        </button>
        {/* Icon-only, and the name is on the control: a square the size of the page header's own
          * primary, with the floppy every editor in this app would draw. */}
        <button
          type="button"
          className={`${String(ember.ember)} ${String(styles.dialogSubmit)}`}
          aria-disabled={complete && !busy ? undefined : true}
          aria-label={saveLabel}
          title={complete ? saveLabel : t('web:reports.editor.newQuestion.incomplete')}
          onClick={() => {
            if (!complete || busy) return;
            onSubmit(
              {
                label: value.label.trim(),
                answerType: value.answerType,
                options: normalizedOptions(value),
                required: value.required,
              },
              existing?.id ?? null,
            );
          }}
        >
          <SaveGlyph className={styles.dialogSubmitIcon} />
        </button>
      </div>
    </>
  );
}
